import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as auth from '../../api/auth'

/**
 * The "continue with Google" affordance.
 *
 * The mark is inlined SVG rather than fetched from Google's CDN: the published
 * pages carry a strict CSP that blocks external hosts outright, and a login
 * button whose icon silently fails to load reads as a broken page on exactly
 * the screen where trust matters most.
 */
type GoogleButtonProps =
  | {
      purpose: auth.OAuthPurpose
      invite?: string
      label?: string
      disabled?: boolean
      onBeforeStart?: () => boolean
      onBusyChange?: (busy: boolean) => void
      onClick?: never
    }
  | {
      purpose: 'link'
      invite?: never
      label?: string
      disabled?: boolean
      onBeforeStart?: never
      onBusyChange?: never
      onClick: () => void
    }

export function GoogleButton({
  purpose,
  invite,
  label,
  disabled = false,
  onBeforeStart,
  onBusyChange,
  onClick,
}: GoogleButtonProps) {
  const { t } = useTranslation()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  async function start() {
    if (disabled) return
    if (purpose === 'link') {
      onClick?.()
      return
    }
    if (onBeforeStart && !onBeforeStart()) return
    setBusy(true)
    onBusyChange?.(true)
    setError('')
    try {
      await auth.startGoogleOAuth(purpose, invite)
    } catch {
      setError(t('auth_errors.oauth_failed'))
      setBusy(false)
      onBusyChange?.(false)
    }
  }

  return (
    <>
      <button
        type="button"
        className="fx-auth-oauth"
        disabled={busy || disabled}
        aria-busy={busy || undefined}
        onClick={() => void start()}
      >
        <GoogleMark />
        <span>{label ?? t('auth.continue_with_google')}</span>
      </button>
      {error && <div className="fx-auth-error" role="alert">{error}</div>}
    </>
  )
}

function GoogleMark() {
  return (
    <svg className="fx-auth-oauth-mark" viewBox="0 0 18 18" aria-hidden="true" focusable="false">
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62Z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18Z"
      />
      <path
        fill="#FBBC05"
        d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33Z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.9 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58Z"
      />
    </svg>
  )
}

/** A labelled rule separating the provider button from the password form. */
export function AuthDivider() {
  const { t } = useTranslation()
  return (
    <div className="fx-auth-divider">
      <span>{t('auth.or')}</span>
    </div>
  )
}
